import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DollarSign, TrendingUp, TrendingDown, Clock, CheckCircle,
  ArrowDownToLine, BarChart3, PieChart, FileText, Wallet,
  RefreshCw, AlertCircle, ExternalLink, Lock, ChevronRight,
  Calendar, Zap, Eye, Music, Image, Video, Mic, Heart,
  Gift, CreditCard, Star, Filter
} from 'lucide-react';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'breakdown', label: 'Breakdown', icon: PieChart },
  { id: 'content', label: 'Per Content', icon: FileText },
  { id: 'payouts', label: 'Payouts', icon: Wallet },
];

const SOURCE_LABELS = {
  tip: { label: 'Tips', icon: Heart, color: '#F43F5E' },
  gift: { label: 'Gifts', icon: Gift, color: '#EC4899' },
  subscription: { label: 'Subscriptions', icon: Star, color: '#8B5CF6' },
  content_lock: { label: 'Content Locks', icon: Lock, color: '#F59E0B' },
  boost: { label: 'Boosts', icon: Zap, color: '#06B6D4' },
  other: { label: 'Other', icon: CreditCard, color: '#6B7280' },
};

const MEDIA_ICONS = {
  audio: Music, image: Image, video: Video, podcast: Mic, text: FileText,
};

export default function CreatorEarningsPage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Overview state
  const [overview, setOverview] = useState(null);

  // Breakdown state
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownPeriod, setBreakdownPeriod] = useState('all');

  // Timeline state
  const [timeline, setTimeline] = useState(null);
  const [timelineRange, setTimelineRange] = useState('30d');

  // Per-content state
  const [contentEarnings, setContentEarnings] = useState(null);
  const [contentSort, setContentSort] = useState('total');

  // Payouts state
  const [payouts, setPayouts] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutMsg, setPayoutMsg] = useState({ type: '', text: '' });

  const headers = getAuthHeaders();

  const fetchOverview = useCallback(async () => {
    try {
      const [ovRes, tlRes] = await Promise.all([
        fetch(`${API}/earnings/overview`, { headers }),
        fetch(`${API}/earnings/timeline?range=${timelineRange}`, { headers }),
      ]);
      if (ovRes.ok) setOverview(await ovRes.json());
      if (tlRes.ok) setTimeline(await tlRes.json());
    } catch (e) { console.error('Earnings overview fetch error:', e); }
    finally { setLoading(false); }
  }, [timelineRange]);

  const fetchBreakdown = useCallback(async () => {
    try {
      const res = await fetch(`${API}/earnings/breakdown?period=${breakdownPeriod}`, { headers });
      if (res.ok) setBreakdown(await res.json());
    } catch (e) { console.error('Breakdown fetch error:', e); }
  }, [breakdownPeriod]);

  const fetchContent = useCallback(async () => {
    try {
      const res = await fetch(`${API}/earnings/per-content?sort_by=${contentSort}&limit=20`, { headers });
      if (res.ok) setContentEarnings(await res.json());
    } catch (e) { console.error('Content earnings fetch error:', e); }
  }, [contentSort]);

  const fetchPayouts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/earnings/payouts`, { headers });
      if (res.ok) setPayouts(await res.json());
    } catch (e) { console.error('Payouts fetch error:', e); }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  useEffect(() => {
    if (activeTab === 'breakdown') fetchBreakdown();
    if (activeTab === 'content') fetchContent();
    if (activeTab === 'payouts') fetchPayouts();
  }, [activeTab, fetchBreakdown, fetchContent, fetchPayouts]);

  const handlePayout = async () => {
    const amt = parseFloat(payoutAmount);
    if (isNaN(amt) || amt <= 0) { setPayoutMsg({ type: 'error', text: 'Enter a valid amount' }); return; }
    setPayoutLoading(true);
    setPayoutMsg({ type: '', text: '' });
    try {
      const res = await fetch(`${API}/earnings/payout-request`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_usd: amt }),
      });
      if (res.ok) {
        const data = await res.json();
        setPayoutMsg({ type: 'success', text: `Payout of $${amt.toFixed(2)} initiated! Est. arrival: ${new Date(data.estimated_arrival).toLocaleDateString()}` });
        setPayoutAmount('');
        fetchPayouts();
        fetchOverview();
      } else {
        const err = await res.json();
        setPayoutMsg({ type: 'error', text: err.detail || 'Payout failed' });
      }
    } catch { setPayoutMsg({ type: 'error', text: 'Network error' }); }
    finally { setPayoutLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground pb-20">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="earnings-page-title">
              <div className="p-2 rounded-lg bg-gradient-to-br from-violet-600 to-purple-700">
                <DollarSign className="w-6 h-6" />
              </div>
              Creator Earnings
            </h1>
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchOverview(); }} data-testid="earnings-refresh-btn" className="border-border text-muted-foreground hover:text-foreground">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
          <p className="text-muted-foreground text-sm">Track revenue, manage payouts, and see what content earns the most.</p>

          {/* Tabs */}
          <div className="flex gap-1 mt-5 overflow-x-auto scrollbar-hide" data-testid="earnings-tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`earnings-tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all whitespace-nowrap ${active ? 'bg-background text-foreground border-t-2 border-violet-500' : 'text-muted-foreground hover:text-foreground/80 hover:bg-muted/30'}`}
                >
                  <Icon className="w-4 h-4" /> {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pt-6">
        {activeTab === 'overview' && <OverviewTab overview={overview} timeline={timeline} timelineRange={timelineRange} setTimelineRange={setTimelineRange} fetchOverview={fetchOverview} navigate={navigate} />}
        {activeTab === 'breakdown' && <BreakdownTab breakdown={breakdown} period={breakdownPeriod} setPeriod={(p) => { setBreakdownPeriod(p); }} />}
        {activeTab === 'content' && <ContentTab data={contentEarnings} sort={contentSort} setSort={setContentSort} navigate={navigate} />}
        {activeTab === 'payouts' && <PayoutsTab payouts={payouts} payoutAmount={payoutAmount} setPayoutAmount={setPayoutAmount} handlePayout={handlePayout} payoutLoading={payoutLoading} payoutMsg={payoutMsg} navigate={navigate} />}
      </div>
    </div>
  );
}


/* ═══════════════════ OVERVIEW TAB ═══════════════════ */
function OverviewTab({ overview, timeline, timelineRange, setTimelineRange, fetchOverview, navigate }) {
  if (!overview) return null;
  const { lifetime, pending, available, withdrawn, this_month, growth_pct, today, payout_connected, recent_activity } = overview;

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Top metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Total Earned" value={`$${lifetime.total_net.toFixed(2)}`} sub={`${lifetime.transaction_count} transactions`} icon={DollarSign} color="green" testId="metric-total-earned" />
        <MetricCard label="This Month" value={`$${this_month.amount.toFixed(2)}`} sub={growth_pct !== 0 ? `${growth_pct > 0 ? '+' : ''}${growth_pct}% vs last month` : 'No change'} icon={growth_pct >= 0 ? TrendingUp : TrendingDown} color={growth_pct >= 0 ? 'emerald' : 'red'} testId="metric-this-month" />
        <MetricCard label="Available" value={`$${available.toFixed(2)}`} sub="Ready to withdraw" icon={CheckCircle} color="violet" testId="metric-available" />
        <MetricCard label="Pending" value={`$${pending.amount.toFixed(2)}`} sub={`${pending.count} clearing`} icon={Clock} color="amber" testId="metric-pending" />
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-3 bg-card border-border">
          <p className="text-xs text-muted-foreground">Today</p>
          <p className="text-lg font-bold text-foreground">${today.amount.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground">{today.count} transactions</p>
        </Card>
        <Card className="p-3 bg-card border-border">
          <p className="text-xs text-muted-foreground">Withdrawn</p>
          <p className="text-lg font-bold text-blue-400">${withdrawn.toFixed(2)}</p>
        </Card>
        <Card className="p-3 bg-card border-border">
          <p className="text-xs text-muted-foreground">Platform Fees</p>
          <p className="text-lg font-bold text-muted-foreground">${lifetime.total_fees.toFixed(2)}</p>
        </Card>
        <Card className="p-3 bg-card border-border flex items-center gap-2">
          {payout_connected ? (
            <><CheckCircle className="w-4 h-4 text-green-400 shrink-0" /><div><p className="text-xs text-muted-foreground">Payout Method</p><p className="text-sm font-medium text-green-400">Connected</p></div></>
          ) : (
            <><Lock className="w-4 h-4 text-amber-400 shrink-0" /><div><p className="text-xs text-muted-foreground">Payout Method</p><p className="text-sm font-medium text-amber-400">Not Set Up</p></div></>
          )}
        </Card>
      </div>

      {/* Earnings Chart */}
      <Card className="p-5 bg-card border-border" data-testid="earnings-chart-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">Earnings Over Time</h2>
          <div className="flex gap-1">
            {['7d', '30d', '90d', '12m'].map(r => (
              <button key={r} onClick={() => setTimelineRange(r)} data-testid={`timeline-range-${r}`}
                className={`px-3 py-1 rounded text-xs font-medium transition-all ${timelineRange === r ? 'bg-violet-600 text-white' : 'bg-muted/30 text-muted-foreground hover:text-foreground'}`}>
                {r === '12m' ? '1Y' : r.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        {timeline && timeline.points && <MiniBarChart points={timeline.points} />}
      </Card>

      {/* Recent Activity */}
      <Card className="p-5 bg-card border-border" data-testid="recent-activity-card">
        <h2 className="text-base font-semibold mb-3">Recent Activity</h2>
        {(!recent_activity || recent_activity.length === 0) ? (
          <p className="text-muted-foreground text-sm text-center py-6">No earnings yet. Start creating to earn!</p>
        ) : (
          <div className="space-y-2">
            {recent_activity.map((tx, i) => {
              const src = SOURCE_LABELS[tx.source_type] || SOURCE_LABELS.other;
              const Icon = src.icon;
              return (
                <div key={tx.entry_id || i} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 hover:bg-muted/25 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${src.color}20` }}>
                      <Icon className="w-4 h-4" style={{ color: src.color }} />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{src.label} from {tx.from_user_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <span className="text-green-400 font-bold text-sm">+${tx.net_amount_usd?.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}


/* ═══════════════════ BREAKDOWN TAB ═══════════════════ */
function BreakdownTab({ breakdown, period, setPeriod }) {
  const periods = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: '7 Days' },
    { key: 'month', label: 'This Month' },
    { key: 'all', label: 'All Time' },
  ];

  return (
    <div className="space-y-6" data-testid="breakdown-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Revenue by Source</h2>
        <div className="flex gap-1">
          {periods.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`breakdown-period-${p.key}`}
              className={`px-3 py-1 rounded text-xs font-medium transition-all ${period === p.key ? 'bg-violet-600 text-white' : 'bg-muted/30 text-muted-foreground hover:text-foreground'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {breakdown && breakdown.breakdown.length > 0 ? (
        <>
          {/* Visual bar breakdown */}
          <Card className="p-5 bg-card border-border">
            <div className="flex items-center justify-between mb-4">
              <p className="text-muted-foreground text-sm">Total: <span className="text-foreground font-bold">${breakdown.total_net.toFixed(2)}</span></p>
            </div>
            {/* Stacked bar */}
            <div className="h-4 rounded-full overflow-hidden flex bg-muted/30 mb-4">
              {breakdown.breakdown.map((item, i) => {
                const src = SOURCE_LABELS[item.source] || SOURCE_LABELS.other;
                return <div key={i} style={{ width: `${Math.max(item.percentage, 2)}%`, backgroundColor: src.color }} className="transition-all" title={`${src.label}: ${item.percentage}%`} />;
              })}
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-3">
              {breakdown.breakdown.map((item, i) => {
                const src = SOURCE_LABELS[item.source] || SOURCE_LABELS.other;
                return (
                  <div key={i} className="flex items-center gap-1.5 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: src.color }} />
                    <span className="text-muted-foreground">{src.label}</span>
                    <span className="text-foreground font-medium">{item.percentage}%</span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Detailed source cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {breakdown.breakdown.map((item, i) => {
              const src = SOURCE_LABELS[item.source] || SOURCE_LABELS.other;
              const Icon = src.icon;
              return (
                <Card key={i} className="p-4 bg-card border-border hover:border-border transition-colors" data-testid={`breakdown-source-${item.source}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${src.color}20` }}>
                      <Icon className="w-5 h-5" style={{ color: src.color }} />
                    </div>
                    <div>
                      <p className="font-medium">{src.label}</p>
                      <p className="text-xs text-muted-foreground">{item.count} transactions</p>
                    </div>
                    <p className="ml-auto text-lg font-bold text-green-400">${item.net.toFixed(2)}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><p className="text-muted-foreground">Gross</p><p className="font-medium">${item.gross.toFixed(2)}</p></div>
                    <div><p className="text-muted-foreground">Fees</p><p className="font-medium text-muted-foreground">${item.fees.toFixed(2)}</p></div>
                    <div><p className="text-muted-foreground">Avg</p><p className="font-medium">${item.avg.toFixed(2)}</p></div>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        <Card className="p-8 bg-card border-border text-center">
          <PieChart className="w-12 h-12 text-muted-foreground/60 mx-auto mb-3" />
          <p className="text-muted-foreground">No earnings data for this period.</p>
        </Card>
      )}
    </div>
  );
}


/* ═══════════════════ PER-CONTENT TAB ═══════════════════ */
function ContentTab({ data, sort, setSort, navigate }) {
  const sorts = [
    { key: 'total', label: 'Highest Earning' },
    { key: 'recent', label: 'Most Recent' },
    { key: 'count', label: 'Most Transactions' },
  ];

  return (
    <div className="space-y-4" data-testid="content-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Per-Content Revenue</h2>
        <div className="flex gap-1">
          {sorts.map(s => (
            <button key={s.key} onClick={() => setSort(s.key)} data-testid={`content-sort-${s.key}`}
              className={`px-3 py-1 rounded text-xs font-medium transition-all ${sort === s.key ? 'bg-violet-600 text-white' : 'bg-muted/30 text-muted-foreground hover:text-foreground'}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {data && data.items && data.items.length > 0 ? (
        <>
          <p className="text-xs text-muted-foreground">{data.total_posts_with_earnings} posts with earnings</p>
          <div className="space-y-2">
            {data.items.map((item, i) => {
              const MediaIcon = MEDIA_ICONS[item.media_type] || FileText;
              return (
                <Card key={item.post_id} className="p-4 bg-card border-border hover:border-border transition-colors cursor-pointer" data-testid={`content-item-${i}`}
                  onClick={() => navigate(`/post/${item.post_id}`)}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted/30 shrink-0">
                      <MediaIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.content_preview || 'Untitled Post'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">{item.transaction_count} txns</span>
                        <span className="text-xs text-muted-foreground/80">|</span>
                        {item.sources.map(s => {
                          const src = SOURCE_LABELS[s] || SOURCE_LABELS.other;
                          return <span key={s} className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${src.color}15`, color: src.color }}>{src.label}</span>;
                        })}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-bold text-green-400">${item.total_net.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{item.last_earned ? new Date(item.last_earned).toLocaleDateString() : ''}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/80 shrink-0" />
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        <Card className="p-8 bg-card border-border text-center">
          <FileText className="w-12 h-12 text-muted-foreground/60 mx-auto mb-3" />
          <p className="text-muted-foreground">No per-content earnings yet.</p>
          <p className="text-xs text-muted-foreground/80 mt-1">Start earning from tips, content locks, and subscriptions!</p>
        </Card>
      )}
    </div>
  );
}


/* ═══════════════════ PAYOUTS TAB ═══════════════════ */
function PayoutsTab({ payouts, payoutAmount, setPayoutAmount, handlePayout, payoutLoading, payoutMsg, navigate }) {
  if (!payouts) return null;

  const { withdrawals, status_summary, payout_connected, available_balance, min_payout } = payouts;
  const completed = status_summary?.completed || { total: 0, count: 0 };
  const processing = status_summary?.processing || { total: 0, count: 0 };

  return (
    <div className="space-y-6" data-testid="payouts-tab">
      {/* Payout summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Available" value={`$${available_balance.toFixed(2)}`} sub="Ready to withdraw" icon={CheckCircle} color="violet" testId="payout-available" />
        <MetricCard label="Completed" value={`$${completed.total.toFixed(2)}`} sub={`${completed.count} payouts`} icon={ArrowDownToLine} color="green" testId="payout-completed" />
        <MetricCard label="Processing" value={`$${processing.total.toFixed(2)}`} sub={`${processing.count} in transit`} icon={Clock} color="blue" testId="payout-processing" />
        <Card className="p-3 bg-card border-border flex items-center gap-3">
          {payout_connected ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Lock className="w-5 h-5 text-amber-400" />}
          <div>
            <p className="text-xs text-muted-foreground">Stripe Connect</p>
            <p className={`text-sm font-medium ${payout_connected ? 'text-green-400' : 'text-amber-400'}`}>
              {payout_connected ? 'Connected' : 'Not Set Up'}
            </p>
          </div>
        </Card>
      </div>

      {/* Request Payout */}
      <Card className="p-5 bg-card border-border" data-testid="request-payout-card">
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
          <ArrowDownToLine className="w-5 h-5" /> Request Payout
        </h2>
        {!payout_connected ? (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-400 text-sm">Payout Method Required</p>
                <p className="text-xs text-muted-foreground mt-1 mb-3">Connect Stripe to withdraw your earnings.</p>
                <Button size="sm" onClick={() => navigate('/dashboard?tab=earnings')} data-testid="setup-stripe-btn">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Set Up Stripe
                </Button>
              </div>
            </div>
          </div>
        ) : available_balance < min_payout ? (
          <div className="bg-muted/30 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground">Minimum payout is <span className="text-foreground font-medium">${min_payout.toFixed(2)}</span></p>
            <p className="text-xs text-muted-foreground mt-1">Available: ${available_balance.toFixed(2)}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="number" placeholder={`Min $${min_payout.toFixed(0)}`} value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)}
                  className="pl-8 bg-muted/30 border-border" max={available_balance} data-testid="payout-amount-input" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setPayoutAmount(available_balance.toFixed(2))} data-testid="payout-max-btn" className="border-border text-muted-foreground">Max</Button>
              <Button onClick={handlePayout} disabled={payoutLoading || !payoutAmount} data-testid="payout-submit-btn" className="bg-violet-600 hover:bg-violet-700">
                {payoutLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Withdraw'}
              </Button>
            </div>
            {payoutMsg.text && (
              <div className={`flex items-center gap-2 text-sm ${payoutMsg.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                {payoutMsg.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                {payoutMsg.text}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Payout History */}
      <Card className="p-5 bg-card border-border" data-testid="payout-history-card">
        <h2 className="text-base font-semibold mb-3">Payout History</h2>
        {withdrawals.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-6">No payouts yet.</p>
        ) : (
          <div className="space-y-2">
            {withdrawals.map(wd => {
              const statusColors = { completed: 'text-green-400 bg-green-500/10', processing: 'text-blue-400 bg-blue-500/10', pending: 'text-amber-400 bg-amber-500/10', rejected: 'text-red-400 bg-red-500/10' };
              const sc = statusColors[wd.status] || statusColors.pending;
              return (
                <div key={wd.withdrawal_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${sc.split(' ')[1]}`}>
                      {wd.status === 'completed' ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Clock className="w-4 h-4 text-blue-400" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">Payout #{wd.withdrawal_id.slice(-6)}</p>
                      <p className="text-xs text-muted-foreground">{new Date(wd.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm">${wd.amount_usd?.toFixed(2)}</p>
                    <p className={`text-xs capitalize ${sc.split(' ')[0]}`}>{wd.status}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}


/* ═══════════════════ SHARED COMPONENTS ═══════════════════ */

function MetricCard({ label, value, sub, icon: Icon, color, testId }) {
  const colorMap = {
    green: { bg: 'bg-green-500/10', text: 'text-green-400' },
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
    violet: { bg: 'bg-violet-500/10', text: 'text-violet-400' },
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
    blue: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
    red: { bg: 'bg-red-500/10', text: 'text-red-400' },
  };
  const c = colorMap[color] || colorMap.green;

  return (
    <Card className="p-3 bg-card border-border" data-testid={testId}>
      <div className="flex items-center gap-2.5">
        <div className={`p-2 rounded-lg ${c.bg} shrink-0`}>
          <Icon className={`w-4 h-4 ${c.text}`} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">{label}</p>
          <p className={`text-lg font-bold ${c.text}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

function MiniBarChart({ points }) {
  if (!points || points.length === 0) return <p className="text-muted-foreground text-sm text-center py-4">No data yet</p>;

  const maxVal = Math.max(...points.map(p => p.net), 1);
  const total = points.reduce((s, p) => s + p.net, 0);

  return (
    <div data-testid="earnings-mini-chart">
      <div className="flex items-end gap-[2px] h-32">
        {points.map((p, i) => {
          const h = Math.max((p.net / maxVal) * 100, 2);
          return (
            <div key={i} className="flex-1 group relative flex flex-col justify-end">
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-muted text-foreground text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                ${p.net.toFixed(2)} | {p.date}
              </div>
              <div
                className="w-full rounded-t transition-all bg-violet-600 hover:bg-violet-500"
                style={{ height: `${h}%`, minHeight: '2px' }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-xs text-muted-foreground/80">
        <span>{points[0]?.date}</span>
        <span className="text-muted-foreground font-medium">Total: ${total.toFixed(2)}</span>
        <span>{points[points.length - 1]?.date}</span>
      </div>
    </div>
  );
}
